'use client'

import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Image from 'next/image';
import Link from 'next/link';

export default function Sobre() {
  return (
    <main className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">Sobre Raquel Luqueci</h1>
              <p className="text-lg text-gray-600 mb-4">
                Fisioterapeuta especializada em reabilitação neurológica, dedicada a melhorar a qualidade de vida dos pacientes através de tratamentos personalizados e baseados em evidências científicas.
              </p>
              <p className="text-lg text-gray-600">
                Com formação pela Escola Superior de Saúde do Alcoitão (ESSAlcoitão) e pós-graduação em demência, Raquel combina conhecimento técnico avançado com uma abordagem humanizada para cada paciente.
              </p>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <div className="absolute inset-0 bg-teal-600"></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Biography Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Minha Trajetória</h2>
            
            <div className="prose prose-lg mx-auto">
              <p>
                Minha jornada na fisioterapia começou com uma paixão por ajudar pessoas a recuperarem sua independência e qualidade de vida. Após me formar em Fisioterapia, especializei-me em reabilitação neurológica, área que me fascina pela complexidade e pelo impacto transformador que podemos ter na vida dos pacientes.
              </p>
              
              <p>
                Realizei minha pós-graduação em demência na Escola Superior de Saúde do Alcoitão (ESSAlcoitão), uma das instituições mais respeitadas em Portugal na área da saúde. Esta formação me proporcionou conhecimentos aprofundados sobre as condições neurológicas e as melhores abordagens terapêuticas baseadas em evidências científicas.
              </p>
              
              <p>
                Ao longo da minha carreira, tive a oportunidade de trabalhar em diversas instituições de saúde, onde pude aprimorar minhas habilidades técnicas e desenvolver uma abordagem personalizada para cada paciente. Acredito firmemente que cada pessoa é única e, portanto, seu tratamento também deve ser.
              </p>
              
              <p>
                Minha especialização em reabilitação neurológica me permite atender pacientes com diversas condições, como AVC (Acidente Vascular Cerebral), doença de Parkinson, esclerose múltipla, traumatismo cranioencefálico, entre outras. Trabalho com técnicas avançadas de reabilitação, sempre buscando a melhor evidência científica disponível para oferecer tratamentos eficazes.
              </p>
              
              <p>
                Além da prática clínica, dedico-me constantemente à atualização profissional, participando de congressos, cursos e workshops para incorporar as mais recentes descobertas e técnicas ao meu trabalho. Acredito que a educação continuada é fundamental para oferecer o melhor atendimento possível aos meus pacientes.
              </p>
              
              <p>
                Meu objetivo é proporcionar não apenas a reabilitação física, mas também apoio emocional e orientação para pacientes e familiares, criando um ambiente acolhedor onde todos se sintam confortáveis e confiantes no processo de recuperação.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Education & Certifications */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Formação e Certificações</h2>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-900">Pós-Graduação em Demência</h3>
                    <p className="text-gray-600">Escola Superior de Saúde do Alcoitão (ESSAlcoitão)</p>
                    <p className="text-gray-500">2020 - 2021</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-900">Licenciatura em Fisioterapia</h3>
                    <p className="text-gray-600">Escola Superior de Saúde do Alcoitão (ESSAlcoitão)</p>
                    <p className="text-gray-500">2015 - 2019</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-900">Certificação em Reabilitação Neurológica Avançada</h3>
                    <p className="text-gray-600">Instituto Internacional de Neurociências</p>
                    <p className="text-gray-500">2022</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-900">Especialização em Reabilitação de AVC</h3>
                    <p className="text-gray-600">Centro de Estudos Avançados em Fisioterapia</p>
                    <p className="text-gray-500">2021</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Approach Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Minha Abordagem</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Acredito em uma abordagem holística e personalizada, que considera não apenas os aspectos físicos, mas também emocionais e sociais de cada paciente.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Avaliação Detalhada</h3>
              <p className="text-gray-600">
                Cada tratamento começa com uma avaliação minuciosa para identificar as necessidades específicas e estabelecer objetivos realistas e mensuráveis.
              </p>
            </div>
            
            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Tratamento Personalizado</h3>
              <p className="text-gray-600">
                Desenvolvo planos de tratamento individualizados, adaptados às necessidades, objetivos e preferências de cada paciente.
              </p>
            </div>
            
            <div className="bg-teal-50 p-6 rounded-lg">
              <div className="h-12 w-12 flex items-center justify-center bg-teal-100 rounded-md text-teal-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Baseado em Evidências</h3>
              <p className="text-gray-600">
                Utilizo técnicas e métodos respaldados pela ciência, garantindo tratamentos eficazes e seguros para cada condição.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Video Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Conheça Meu Trabalho</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Assista ao vídeo de apresentação para conhecer mais sobre minha abordagem e como posso ajudar você.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="aspect-w-16 aspect-h-9 bg-gray-300 rounded-lg shadow-lg">
              {/* Placeholder for video */}
              <div className="flex items-center justify-center h-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Pronto para iniciar sua jornada de recuperação?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Agende sua avaliação inicial e descubra como posso ajudar você a recuperar sua saúde e bem-estar.
          </p>
          <Link
            href="/agenda"
            className="px-8 py-3 bg-white hover:bg-gray-100 text-teal-800 font-medium rounded-md transition-colors duration-300 inline-block"
          >
            Agendar Consulta
          </Link>
        </div>
      </section>
      
      <Footer />
    </main>
  );
}
